import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Bookmark, CreateBookmarkRequest, UpdateBookmarkRequest } from '../../types/bookmark';
import * as bookmarkService from '../../services/bookmarkService';

interface BookmarksState {
  bookmarks: Bookmark[];
  isLoading: boolean;
  error: string | null;
  saveDialogOpen: boolean;
  editDialogOpen: boolean;
  editingBookmark: Bookmark | null;
  deleteConfirmOpen: boolean;
  deletingBookmarkId: string | null;
}

const initialState: BookmarksState = {
  bookmarks: [],
  isLoading: false,
  error: null,
  saveDialogOpen: false,
  editDialogOpen: false,
  editingBookmark: null,
  deleteConfirmOpen: false,
  deletingBookmarkId: null,
};

// Async thunks
export const loadBookmarks = createAsyncThunk(
  'bookmarks/loadBookmarks',
  async (_, { rejectWithValue }) => {
    try {
      return await bookmarkService.fetchBookmarks();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load bookmarks');
    }
  }
);

export const saveBookmark = createAsyncThunk(
  'bookmarks/saveBookmark',
  async (request: CreateBookmarkRequest, { rejectWithValue }) => {
    try {
      return await bookmarkService.createBookmark(request);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save bookmark');
    }
  }
);

export const editBookmark = createAsyncThunk(
  'bookmarks/editBookmark',
  async ({ id, request }: { id: string; request: UpdateBookmarkRequest }, { rejectWithValue }) => {
    try {
      return await bookmarkService.updateBookmark(id, request);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update bookmark');
    }
  }
);

export const removeBookmark = createAsyncThunk(
  'bookmarks/removeBookmark',
  async (bookmarkId: string, { rejectWithValue }) => {
    try {
      await bookmarkService.deleteBookmark(bookmarkId);
      return bookmarkId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete bookmark');
    }
  }
);

const bookmarksSlice = createSlice({
  name: 'bookmarks',
  initialState,
  reducers: {
    openSaveDialog: (state) => {
      state.saveDialogOpen = true;
    },
    closeSaveDialog: (state) => {
      state.saveDialogOpen = false;
    },
    openEditDialog: (state, action: PayloadAction<Bookmark>) => {
      state.editDialogOpen = true;
      state.editingBookmark = action.payload;
    },
    closeEditDialog: (state) => {
      state.editDialogOpen = false;
      state.editingBookmark = null;
    },
    openDeleteConfirm: (state, action: PayloadAction<string>) => {
      state.deleteConfirmOpen = true;
      state.deletingBookmarkId = action.payload;
    },
    closeDeleteConfirm: (state) => {
      state.deleteConfirmOpen = false;
      state.deletingBookmarkId = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load bookmarks
      .addCase(loadBookmarks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadBookmarks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bookmarks = action.payload;
      })
      .addCase(loadBookmarks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save bookmark
      .addCase(saveBookmark.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveBookmark.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bookmarks.unshift(action.payload);
        state.saveDialogOpen = false;
      })
      .addCase(saveBookmark.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Edit bookmark
      .addCase(editBookmark.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editBookmark.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.bookmarks.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) {
          state.bookmarks[index] = action.payload;
        }
        state.editDialogOpen = false;
        state.editingBookmark = null;
      })
      .addCase(editBookmark.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Remove bookmark
      .addCase(removeBookmark.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeBookmark.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bookmarks = state.bookmarks.filter((b) => b.id !== action.payload);
        state.deleteConfirmOpen = false;
        state.deletingBookmarkId = null;
      })
      .addCase(removeBookmark.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  openSaveDialog,
  closeSaveDialog,
  openEditDialog,
  closeEditDialog,
  openDeleteConfirm,
  closeDeleteConfirm,
  clearError,
} = bookmarksSlice.actions;

export default bookmarksSlice.reducer;
